// frontend/src/components/workflows/WorkflowConstraints.tsx - FIXED naming conflict
import React, { useCallback } from 'react';
import { Input } from '@/components/ui/Input';
import { cn } from '@/utils';

// Import workflow constraints type with alias to avoid naming conflict
import type { WorkflowConstraints as WorkflowConstraintsType } from './types';

interface WorkflowConstraintsProps {
  constraints: WorkflowConstraintsType;
  onConstraintUpdate: (field: keyof WorkflowConstraintsType, value: any) => void;
  errors?: { [field: string]: string };
}

export const WorkflowConstraintsComponent: React.FC<WorkflowConstraintsProps> = ({
  constraints,
  onConstraintUpdate,
  errors = {}
}) => {
  const handleDepartmentsChange = useCallback((value: string) => {
    const departments = value
      .split(',')
      .map(dept => dept.trim())
      .filter(dept => dept.length > 0);
    onConstraintUpdate('allowedDepartments', departments);
  }, [onConstraintUpdate]);

  const departmentsString = (constraints.allowedDepartments || []).join(', ');

  const roleOptions = [
    { 
      value: 'contributor', 
      label: 'Contributor', 
      description: 'All users can use this workflow' 
    },
    { 
      value: 'admin', 
      label: 'Admin', 
      description: 'Only admins and implementors can use this workflow' 
    },
    { 
      value: 'implementor', 
      label: 'Implementor', 
      description: 'Only implementors can use this workflow' 
    }
  ];

  return (
    <div className="space-y-6">
      {/* Performance Constraints */}
      <div>
        <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Performance Constraints
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Max Parallel Responses */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Maximum Parallel Responses
              <span className="text-xs text-gray-500 ml-1">(1-1000)</span>
            </label>
            <Input
              type="number"
              min="1"
              max="1000"
              value={constraints.maxParallelResponses || 100}
              onChange={(e) => onConstraintUpdate('maxParallelResponses', parseInt(e.target.value) || 100)}
              className={errors.maxParallelResponses ? 'border-red-500 focus:border-red-500' : ''}
              placeholder="100"
            />
            {errors.maxParallelResponses && (
              <p className="text-red-500 text-xs mt-1">{errors.maxParallelResponses}</p>
            )}
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Maximum number of responses that can be processed simultaneously
            </p>
          </div>

          {/* Global Time Limit */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Global Time Limit (hours)
              <span className="text-xs text-gray-500 ml-1">(optional)</span>
            </label>
            <Input
              type="number"
              min="1"
              max="8760"
              value={constraints.globalTimeLimit || ''}
              onChange={(e) => onConstraintUpdate('globalTimeLimit', 
                e.target.value ? parseInt(e.target.value) || undefined : undefined
              )}
              className={errors.globalTimeLimit ? 'border-red-500 focus:border-red-500' : ''}
              placeholder="168"
            />
            {errors.globalTimeLimit && (
              <p className="text-red-500 text-xs mt-1">{errors.globalTimeLimit}</p>
            )}
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Maximum time allowed for the entire workflow (overrides individual stage limits)
            </p>
          </div>
        </div>
      </div>

      {/* Access Controls */}
      <div>
        <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Access Controls
        </h4>

        <div className="space-y-4">
          {/* Minimum User Role */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Minimum User Role
            </label>
            <div className="space-y-2">
              {roleOptions.map(role => (
                <div key={role.value} className="flex items-start space-x-3">
                  <input
                    type="radio"
                    id={`role-${role.value}`}
                    name="minimumUserRole"
                    value={role.value}
                    checked={constraints.minimumUserRole === role.value}
                    onChange={(e) => onConstraintUpdate('minimumUserRole', e.target.value)}
                    className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500 mt-1"
                  />
                  <div className="flex-1">
                    <label 
                      htmlFor={`role-${role.value}`}
                      className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer"
                    >
                      {role.label}
                    </label>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {role.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            {errors.minimumUserRole && (
              <p className="text-red-500 text-xs mt-2">{errors.minimumUserRole}</p>
            )}
          </div>

          {/* Allowed Departments */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Allowed Departments
              <span className="text-xs text-gray-500 ml-1">(optional)</span>
            </label>
            <Input
              type="text"
              value={departmentsString}
              onChange={(e) => handleDepartmentsChange(e.target.value)}
              className={errors.allowedDepartments ? 'border-red-500 focus:border-red-500' : ''}
              placeholder="Marketing, Sales, Support"
            />
            {errors.allowedDepartments && (
              <p className="text-red-500 text-xs mt-1">{errors.allowedDepartments}</p>
            )}
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Comma-separated list of departments that can use this workflow. Leave empty to allow all departments.
            </p>
            
            {/* Department Tags Display */}
            {constraints.allowedDepartments && constraints.allowedDepartments.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {constraints.allowedDepartments.map((dept, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400 rounded-full"
                  >
                    {dept}
                    <button
                      type="button"
                      onClick={() => {
                        const newDepartments = (constraints.allowedDepartments || []).filter((_, i) => i !== index);
                        onConstraintUpdate('allowedDepartments', newDepartments);
                      }}
                      className="ml-1 inline-flex items-center justify-center w-3 h-3 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
                    >
                      <svg className="w-2 h-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Draft Locking */}
      <div>
        <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Draft Locking
        </h4>

        <div className="space-y-4">
          {/* Enable Draft Locking */}
          <div className="flex items-start space-x-3 p-4 bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg">
            <input
              type="checkbox"
              id="draftLockingEnabled"
              checked={constraints.draftLocking?.enabled || false}
              onChange={(e) => {
                onConstraintUpdate('draftLocking', {
                  enabled: e.target.checked,
                  timeout: constraints.draftLocking?.timeout || { value: 24, unit: 'hours' },
                  allowReleaseRequest: constraints.draftLocking?.allowReleaseRequest ?? true
                });
              }}
              className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-0.5"
            />
            <div className="flex-1">
              <label
                htmlFor="draftLockingEnabled"
                className="text-sm font-medium text-gray-900 dark:text-white cursor-pointer"
              >
                Enable Draft Locking
              </label>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                When enabled, only the user who creates or first edits a draft can continue editing it.
                Other users in the approval group must request release to edit.
              </p>
            </div>
          </div>

          {/* Draft Lock Timeout - Only shown when enabled */}
          {constraints.draftLocking?.enabled && (
            <div className="ml-8 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Timeout Value */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Lock Timeout Duration
                  </label>
                  <Input
                    type="number"
                    min="1"
                    max="999"
                    value={constraints.draftLocking?.timeout?.value || 24}
                    onChange={(e) => {
                      const value = parseInt(e.target.value) || 24;
                      onConstraintUpdate('draftLocking', {
                        ...constraints.draftLocking,
                        enabled: true,
                        timeout: {
                          value,
                          unit: constraints.draftLocking?.timeout?.unit || 'hours'
                        }
                      });
                    }}
                    placeholder="24"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    How long the draft remains locked
                  </p>
                </div>

                {/* Timeout Unit */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Time Unit
                  </label>
                  <select
                    value={constraints.draftLocking?.timeout?.unit || 'hours'}
                    onChange={(e) => {
                      onConstraintUpdate('draftLocking', {
                        ...constraints.draftLocking,
                        enabled: true,
                        timeout: {
                          value: constraints.draftLocking?.timeout?.value || 24,
                          unit: e.target.value as 'minutes' | 'hours' | 'days'
                        }
                      });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                             bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                             focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="minutes">Minutes</option>
                    <option value="hours">Hours</option>
                    <option value="days">Days</option>
                  </select>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    After this time, lock auto-releases
                  </p>
                </div>
              </div>

              {/* Allow Release Requests */}
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  id="allowReleaseRequest"
                  checked={constraints.draftLocking?.allowReleaseRequest ?? true}
                  onChange={(e) => {
                    onConstraintUpdate('draftLocking', {
                      ...constraints.draftLocking,
                      enabled: true,
                      allowReleaseRequest: e.target.checked
                    });
                  }}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-0.5"
                />
                <div className="flex-1">
                  <label
                    htmlFor="allowReleaseRequest"
                    className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer"
                  >
                    Allow Release Requests
                  </label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    When enabled, other users can request the lock owner to release the draft for editing.
                    If disabled, users must wait for the lock to expire.
                  </p>
                </div>
              </div>

              {/* Lock Summary */}
              <div className="p-3 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-600 rounded">
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  <span className="font-medium">Lock Summary:</span> Drafts will be locked for{' '}
                  <span className="font-semibold text-blue-600 dark:text-blue-400">
                    {constraints.draftLocking?.timeout?.value || 24}{' '}
                    {constraints.draftLocking?.timeout?.unit || 'hours'}
                  </span>
                  {constraints.draftLocking?.allowReleaseRequest && (
                    <>, and users can request early release</>
                  )}
                  .
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Advanced Settings */}
      <div>
        <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Advanced Settings
        </h4>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <div className="flex items-start space-x-3">
            <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div className="flex-1">
              <h5 className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-1">
                Performance Impact
              </h5>
              <p className="text-yellow-700 dark:text-yellow-300 text-sm">
                Setting very high parallel limits or very short time limits may impact system performance. 
                Consider your organization's typical workload when configuring these constraints.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Constraints Summary */}
      <div className="p-4 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-600 rounded-lg">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Current Constraints Summary
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Max Parallel:</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {constraints.maxParallelResponses || 100}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Global Time Limit:</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {constraints.globalTimeLimit ? `${constraints.globalTimeLimit}h` : 'None'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Draft Locking:</span>
              <span className={cn(
                'font-medium px-2 py-1 text-xs rounded-full',
                constraints.draftLocking?.enabled
                  ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
                  : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
              )}>
                {constraints.draftLocking?.enabled ? 'Enabled' : 'Disabled'}
              </span>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Min Role:</span>
              <span className={cn(
                'font-medium capitalize px-2 py-1 text-xs rounded-full',
                constraints.minimumUserRole === 'implementor'
                  ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                  : constraints.minimumUserRole === 'admin'
                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400'
                    : 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
              )}>
                {constraints.minimumUserRole || 'contributor'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Departments:</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {constraints.allowedDepartments && constraints.allowedDepartments.length > 0
                  ? `${constraints.allowedDepartments.length} specified`
                  : 'All allowed'
                }
              </span>
            </div>
            {constraints.draftLocking?.enabled && (
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Lock Timeout:</span>
                <span className="font-medium text-gray-900 dark:text-white">
                  {constraints.draftLocking.timeout.value} {constraints.draftLocking.timeout.unit}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Help Text */}
      <div className="text-xs text-gray-500 dark:text-gray-400">
        <p className="font-medium mb-1">Constraint Guidelines:</p>
        <ul className="list-disc list-inside space-y-1">
          <li>Higher parallel limits improve throughput but may impact performance</li>
          <li>Global time limits override individual stage time limits</li>
          <li>Stricter user roles provide better security but may limit workflow usage</li>
          <li>Department restrictions help ensure workflows are used by appropriate teams</li>
          <li>Test constraints with a small group before deploying to all users</li>
        </ul>
      </div>
    </div>
  );
};